import { motion } from 'motion/react';
import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '~/modules/elements/akui/icon';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { formatSongLength } from '~/modules/songs/utils/format-song-length';
import { ordinal } from '~/modules/utils/ordinal';
import { HelpEntry } from '~/routes/keyboard-help/context';
import { ControlDescriptor, renderableControls } from '~/routes/keyboard-help/controls';
import { playerColour } from '~/routes/remote-mic/components/player-colour';
import SongThumbnail from '~/routes/remote-mic/components/song-thumbnail';
import { activateControl, setControlValue } from '~/routes/remote-mic/panels/microphone/remote-actions';
import RemoteControl from '~/routes/remote-mic/panels/microphone/remote-controls';
import { screenTransition } from '~/routes/remote-mic/panels/microphone/screen-transition';

type SelectDescriptor = Extract<ControlDescriptor, { type: 'select' }>;

const BACK = 'exit-song-settings';
const MODE = 'game-mode-setting';
const DIFFICULTY = 'difficulty-setting';
const SETUP_MICS = 'select-inputs-button';
const PLAY = 'play-song-button';
const PRACTICE = 'practice-song-button';
const QUEUE = 'queue-song-button';
const PART = /^player-(\d+)-track-setting$/;

// The figures copy the television's people glyphs: two colours for a duel, three for pass the mic,
// and two in the same colour for co-op - which is the whole difference between co-op and a duel.
const MODE_FIGURES: Record<string, number[]> = {
  DUEL: [0, 1],
  PASS_THE_MIC: [0, 1, 2],
  CO_OP: [0, 0],
};

// Past three options a row of tiles stops fitting a phone, so a longer list (the debug levels) is a
// sheet like any other select.
const MAX_SEGMENTS = 3;

const QUIET = 'bg-black/50 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]';
const CONTROL = 'bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]';
const PRESS = 'cursor-pointer border-none transition-transform duration-100 active:scale-[0.98]';

export default function SongSettingsScreen({ keyboard }: { keyboard: HelpEntry }) {
  const controls = renderableControls(keyboard.controls ?? []);
  const byName = (name: string) => controls.find((control) => control.name === name);
  const queue = useSubscription('song-queue') ?? [];

  const back = byName(BACK);
  const mode = byName(MODE);
  const difficulty = byName(DIFFICULTY);
  const setupMics = byName(SETUP_MICS);
  const play = byName(PLAY);
  const practice = byName(PRACTICE);
  const queueButton = byName(QUEUE);
  const parts = controls.filter((control) => PART.test(control.name));

  const consumed = [back, mode, difficulty, setupMics, play, practice, queueButton, ...parts];
  const leftovers = controls.filter((control) => !consumed.includes(control));

  // With nothing set up the television offers no Play at all, and Setup microphones is the action.
  const notSetUp = !play && !!setupMics;
  const song = keyboard.song;
  const queuePosition = song ? queue.findIndex((entry) => entry.id === song.id) + 1 : 0;

  return (
    <motion.div
      data-test="remote-keyboard"
      data-mode="mirror"
      data-screen="song-settings"
      className="flex h-full min-h-0 w-full flex-col gap-3.5"
      {...screenTransition}>
      <div className="flex shrink-0 items-center gap-3">
        <button
          type="button"
          aria-label={back?.label ?? 'Back to song list'}
          onClick={() => back && activateControl(back.name)}
          disabled={!back || back.disabled}
          data-test={`control-${BACK}`}
          data-control-type="button"
          className={twMerge(
            'flex size-13 shrink-0 items-center justify-center rounded-2xl text-white disabled:opacity-40',
            QUIET,
            PRESS,
          )}>
          <Icon icon="ic:baseline-arrow-back" size={5.5} />
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[0.75rem] tracking-[0.08em] text-white/60 uppercase">Song settings</span>
          {song && (
            <>
              <span data-test="song-settings-title" className="truncate text-[1.125rem] leading-[1.2] font-bold">
                {song.title}
              </span>
              <span className="flex min-w-0 text-[0.875rem] text-white/72">
                <span className="min-w-0 truncate">{song.artist}</span>
                <span className="ml-1 shrink-0 whitespace-nowrap">{songTail(song.duration, song.duet)}</span>
              </span>
            </>
          )}
        </div>
        {song && <SongThumbnail video={song.video} className="h-12 w-16 rounded-[0.625rem]" iconSize={6} />}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain pb-2">
        {notSetUp && (
          <div
            data-test="mics-not-set-up"
            className="flex shrink-0 gap-3 rounded-2xl bg-[rgba(255,165,0,0.14)] px-4 py-3.5 shadow-[inset_0_0_0_1px_rgba(255,165,0,0.5)]">
            <Icon icon="ic:baseline-info" size={5.5} className="text-active mt-px shrink-0" />
            <span className="flex flex-col gap-[0.1875rem]">
              <span className="text-[1rem] font-bold">Microphones are not set up yet</span>
              <span className="text-[0.875rem] leading-[1.4] text-white/85">
                Nothing can be scored until at least one is. Phones work as microphones too.
              </span>
            </span>
          </div>
        )}
        <div className={twMerge('flex shrink-0 flex-col gap-4', notSetUp && 'opacity-45')}>
          {mode?.type === 'select' && (
            <Section label="Mode">
              {mode.options.length <= MAX_SEGMENTS ? <ModeTiles control={mode} /> : <SheetSelect control={mode} />}
            </Section>
          )}
          {difficulty?.type === 'select' && (
            <Section label="Difficulty">
              {difficulty.options.length <= MAX_SEGMENTS ? (
                <DifficultySegments control={difficulty} />
              ) : (
                <SheetSelect control={difficulty} />
              )}
            </Section>
          )}
          {/* An older television sends these two as switches. */}
          {mode && mode.type !== 'select' && <SheetSelect control={mode} />}
          {difficulty && difficulty.type !== 'select' && <SheetSelect control={difficulty} />}
          {parts.length > 0 && (
            <Section label="Parts · one per singer">
              <div className="flex flex-col overflow-hidden rounded-[1.125rem] bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/10">
                {parts.map((control) => (
                  <PartRow key={control.name} control={control} />
                ))}
              </div>
            </Section>
          )}
          {setupMics && !notSetUp && (
            <button
              type="button"
              onClick={() => activateControl(setupMics.name)}
              disabled={setupMics.disabled}
              data-test={`control-${setupMics.name}`}
              data-control-type="button"
              className={twMerge(
                'flex h-15 shrink-0 items-center gap-3 rounded-[1.125rem] px-4.5 text-left text-white disabled:opacity-40',
                QUIET,
                PRESS,
              )}>
              <Icon icon="ic:baseline-settings" size={5.5} className="shrink-0 opacity-80" />
              <span className="min-w-0 flex-1 truncate text-[1.0625rem]">{setupMics.label}</span>
              {setupMics.type === 'button' && setupMics.note && (
                <span className="shrink-0 text-[0.875rem] text-white/60">{setupMics.note}</span>
              )}
              <Icon icon="ic:baseline-chevron-right" size={5.5} className="shrink-0 opacity-70" />
            </button>
          )}
          {leftovers.map((control) => (
            <RemoteControl key={control.name} control={control} onActivate={activateControl} />
          ))}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2.5 border-t border-white/8 pt-3">
        {play && (
          <button
            type="button"
            onClick={() => activateControl(play.name)}
            disabled={play.disabled}
            data-test={`control-${play.name}`}
            data-control-type="button"
            className={twMerge(
              'bg-active flex h-16 items-center justify-center gap-2.5 rounded-[1.125rem] text-[1.1875rem] font-bold text-black disabled:opacity-40',
              PRESS,
            )}>
            <Icon icon="ic:baseline-play-arrow" size={6.5} />
            {play.label}
          </button>
        )}
        {(practice || queueButton) && (
          <div className="flex gap-2.5">
            {practice && (
              <button
                type="button"
                onClick={() => activateControl(practice.name)}
                disabled={practice.disabled}
                data-test={`control-${practice.name}`}
                data-control-type="button"
                className={twMerge(
                  'flex h-14 flex-1 items-center justify-center rounded-2xl text-[1rem] text-white disabled:opacity-40',
                  QUIET,
                  PRESS,
                )}>
                {practice.label}
              </button>
            )}
            {queueButton && (
              <QueueButton control={queueButton} position={queuePosition || queuedFromLabel(queueButton)} />
            )}
          </div>
        )}
        {notSetUp && setupMics && (
          <button
            type="button"
            onClick={() => activateControl(setupMics.name)}
            disabled={setupMics.disabled}
            data-test={`control-${setupMics.name}`}
            data-control-type="button"
            className={twMerge(
              'bg-active flex h-16 items-center justify-center gap-2.5 rounded-[1.125rem] text-[1.1875rem] font-bold text-black disabled:opacity-40',
              PRESS,
            )}>
            <Icon icon="ic:baseline-settings" size={6} />
            {setupMics.label}
          </button>
        )}
      </div>
    </motion.div>
  );
}

const songTail = (duration: number | undefined, duet: boolean) =>
  [formatSongLength(duration), duet ? 'Duet' : undefined]
    .filter(Boolean)
    .map((part) => `· ${part}`)
    .join(' ');

// A television too old to send the song still says "Queued 2nd" on the button itself.
const queuedFromLabel = (control: ControlDescriptor) => {
  const match = /^Queued\D*(\d+)/.exec(control.label);
  return match ? Number(match[1]) : 0;
};

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[0.8125rem] tracking-[0.08em] text-white/70 uppercase">{label}</span>
      {children}
    </div>
  );
}

function ModeTiles({ control }: { control: SelectDescriptor }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-test={`control-${control.name}`} data-control-type="select">
      {control.options.map((option) => {
        const selected = option.value === control.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => !selected && setControlValue(control.name, option.value)}
            disabled={control.disabled || option.disabled}
            data-test={`mode-option-${option.value}`}
            data-selected={selected}
            className={twMerge(
              'flex h-24 min-w-0 flex-col items-center justify-center gap-[0.4375rem] rounded-[0.875rem] px-1.5 disabled:opacity-40',
              PRESS,
              selected ? 'bg-active text-black' : `${CONTROL} text-white`,
            )}>
            <span className="flex gap-px">
              {(MODE_FIGURES[option.value] ?? [0]).map((player, index) => (
                <Icon key={index} icon="ic:baseline-person" size={6} style={{ color: playerColour(player) }} />
              ))}
            </span>
            <span className="flex max-w-full flex-col items-center gap-px">
              <span className="max-w-full truncate text-[0.75rem] font-bold tracking-[0.02em] uppercase">
                {option.label}
              </span>
              {option.note && <span className="text-[0.6875rem] whitespace-nowrap opacity-75">{option.note}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DifficultySegments({ control }: { control: SelectDescriptor }) {
  return (
    <div className="grid grid-cols-3 gap-1.5" data-test={`control-${control.name}`} data-control-type="select">
      {control.options.map((option, index) => {
        const selected = option.value === control.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => !selected && setControlValue(control.name, option.value)}
            disabled={control.disabled || option.disabled}
            data-test={`difficulty-option-${option.value}`}
            data-selected={selected}
            className={twMerge(
              'flex h-13 min-w-0 items-center justify-center gap-2 rounded-[0.875rem] text-[0.875rem] font-bold tracking-[0.02em] uppercase disabled:opacity-40',
              PRESS,
              selected ? 'bg-active text-black' : `${CONTROL} text-white/85`,
            )}>
            <span aria-hidden className="flex gap-[0.1875rem]">
              {Array.from({ length: index + 1 }, (_, dot) => (
                <span key={dot} className="size-1.5 rounded-full bg-current" />
              ))}
            </span>
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function SheetSelect({ control }: { control: ControlDescriptor }) {
  return (
    <div className="flex flex-col overflow-hidden rounded-[1.125rem] bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]">
      <RemoteControl control={control} onActivate={activateControl} inCard />
    </div>
  );
}

function PartRow({ control }: { control: ControlDescriptor }) {
  const player = Number(PART.exec(control.name)?.[1] ?? 0);
  const colour = playerColour(player);

  return (
    <button
      type="button"
      onClick={() => activateControl(control.name)}
      disabled={control.disabled}
      data-test={`control-${control.name}`}
      data-control-type={control.type}
      className="flex h-15 w-full cursor-pointer items-center gap-3 border-none bg-transparent px-4.5 text-left transition-colors duration-150 active:bg-white/6 disabled:opacity-40">
      <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ background: colour }} />
      <span className="ph-no-capture min-w-0 flex-1 truncate text-[1.0625rem] font-bold" style={{ color: colour }}>
        {control.label}
      </span>
      {control.type === 'switch' && (
        <span className="text-active shrink-0 text-[1.0625rem] font-bold">{control.value}</span>
      )}
      <Icon icon="ic:baseline-swap-horiz" size={5.5} className="shrink-0 text-white opacity-70" />
    </button>
  );
}

function QueueButton({ control, position }: { control: ControlDescriptor; position: number }) {
  const queued = position > 0;

  return (
    <button
      type="button"
      onClick={() => activateControl(control.name)}
      disabled={control.disabled}
      data-test={`control-${control.name}`}
      data-control-type="button"
      data-queued={queued}
      className={twMerge(
        'flex h-14 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl leading-[1.15] disabled:opacity-40',
        PRESS,
        queued ? 'bg-green-400/15 text-green-400 shadow-[inset_0_0_0_2px_#4ade80]' : `${QUIET} text-white`,
      )}>
      <span className={twMerge('flex items-center gap-1.5 text-[1rem]', queued && 'font-bold')}>
        {queued && <Icon icon="ic:baseline-check" size={5} />}
        {queued ? `Queued · ${ordinal(position)}` : 'Queue'}
      </span>
      {queued && (
        <span className="text-[0.6875rem] font-semibold tracking-[0.06em] opacity-80">tap again to remove</span>
      )}
    </button>
  );
}
